import { AudioInputControl, BackgroundBlurProvider, ControlBar, ControlBarButton, Phone, Record, useMeetingManager, useRemoteVideoTileState, useRosterState, VideoInputBackgroundBlurControl, VideoTileGrid } from 'amazon-chime-sdk-component-library-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { NoRemoteVideoView } from './components/NoRemoteVideoView';
import { useAttendeeInfo } from './hooks/useAttendeeInfo';
import { useAttendeePresence } from './hooks/useAttendeePresence';
import { useHeartbeat } from './hooks/useHeartbeat';
import { useMeetingObserver } from './hooks/useMeetingObserver';
import { useMeetingSetup } from './hooks/useMeetingSetup';
import { useSpeakingIndicator } from './hooks/speakerIndicator';
import { useStartRecord } from './hooks/startRecord';
import { useStopRecord } from './hooks/stopRecording';
import { useVisibilityChange } from './hooks/useVisibilityChange';
import { PipelineId, ProviderMeetingProps, RecordingResponse } from './types/meeting.types';
import { isRemoteVideoActive, cleanUpMeetingResources } from './utils/mediaUtils';
import { InfoIcon } from '../../svgs/InfoIcon';

export function TempleMeeting({
  meetingResponse,
  attendeeResponse,
  appointmentId,
  setFileName,
  setNoteGenerationStatus,
  setDidCallEnd,
  patientName
}: ProviderMeetingProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [pipeLineId, setPipeLineId] = useState<PipelineId>({} as PipelineId);
  const [callduration, setCallDuration] = useState<string>('00:00:00');


  // Info popup state and ref
  const [showInfo, setShowInfo] = useState(false);
  const infoRef = useRef<HTMLDivElement | null>(null);
  const hideInfoTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleInfoEnter = () => {
    if (hideInfoTimeout.current) {
      clearTimeout(hideInfoTimeout.current);
      hideInfoTimeout.current = null;
    }
    setShowInfo(true);
  };

  const handleInfoLeave = () => {
    hideInfoTimeout.current = setTimeout(() => {
      setShowInfo(false);
    }, 150);
  };

  const navigate = useNavigate();
  const location = useLocation();
  const from = location?.pathname || '/provider/dashboard';

  // Get meeting manager for hooks that need it
  const meetingManager = useMeetingManager();

  // Use custom hooks to handle meeting logic
  const { audioRef } = useMeetingSetup({ meetingResponse, attendeeResponse });
  const { remoteAttendeeId, hasRemoteParticipantLeft } = useAttendeePresence({ attendeeResponse });
  const { attendeeName, attendeeId } = useAttendeeInfo({
    attendeeResponse,
    participantName: patientName
  });

  // Use existing hooks
  const remoteVideoTiles = useRemoteVideoTileState();
  const { roster } = useRosterState();
  const speakingAttendees = useSpeakingIndicator(meetingManager.audioVideo);

  // Use custom hooks for common functionality
  // useVisibilityChange(); // Temporarily disabled for debugging
  useHeartbeat();
  // useMeetingObserver(); // Temporarily disabled for debugging

  const isSpeaking = speakingAttendees[attendeeId];
  const isVideoActive = isRemoteVideoActive(remoteVideoTiles, remoteAttendeeId);

  // Stop recording when remote participant leaves
  useEffect(() => {
    if (hasRemoteParticipantLeft) setIsRecording(false);
  }, [hasRemoteParticipantLeft]);

  const hangUpButtonProps = {
    icon: <Phone className='text-red-500' />,
    onClick: async () => {
      try {
        await cleanUpMeetingResources(meetingManager);
        setDidCallEnd && setDidCallEnd(true);
        navigate(from);
      } catch (error) {
        setDidCallEnd && setDidCallEnd(true);
        navigate(from);
      }
    },
    label: 'Leave',
  };

  const { startRecording, isPending: isStartPending } = useStartRecord();
  const { stopRecording, isPending: isStopPending } = useStopRecord();

  const recordCallUpButtonProps = {
    icon: <Record className={isRecording ? 'text-red-500 md:text-4xl' : 'text-green-500 md:text-4xl'} />,
    onClick: () => {
      if (isRecording && pipeLineId) {
        stopRecording({ appointmentId, pipelineId: pipeLineId.pipelineId, pipelineArn: pipeLineId.pipelineArn }, {
          onSuccess: (data) => {
            setIsRecording(false);
            setFileName && setFileName(data.recording as RecordingResponse);
            setNoteGenerationStatus && setNoteGenerationStatus('ai-generation');
            meetingManager.audioVideo?.realtimeSendDataMessage('recording', JSON.stringify({ isRecording: false }), 1000);
          }
        });
      } else {
        startRecording({ appointmentId }, {
          onSuccess: ({ recording }) => {
            setPipeLineId(recording);
            setIsRecording(true);
            meetingManager.audioVideo?.realtimeSendDataMessage('recording', JSON.stringify({ isRecording: true, startTime: Date.now() }), 1000);
          }
        });
      }
    },
    label: isRecording ? 'Stop Recording' : 'Start Recording',
    disabled: isStartPending || isStopPending,
  };

  // Recording timer
  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (isRecording) {
      const startTime = Date.now();

      timer = setInterval(() => {
        const elapsedTime = Date.now() - startTime;
        const hours = Math.floor((elapsedTime / 3600000) % 24);
        const minutes = Math.floor((elapsedTime / 60000) % 60);
        const seconds = Math.floor((elapsedTime / 1000) % 60);

        setCallDuration(
          `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
        );
      }, 1000);
    }

    return () => {
      clearInterval(timer);
    };
  }, [isRecording]);

  return (
    <div className="text-white h-[82%] w-2/3 mx-auto bottom-2 bg-gray-500">
      <audio id="audio-element" style={{ display: 'none' }} ref={audioRef} />
      <VideoTileGrid
        layout='standard'
        className='w-full h-full bg-gray-500'
        noRemoteVideoView={
          <div className="w-full h-auto shadow-sm rounded-lg mx-auto flex flex-col items-center justify-center font-normal font-gentiumBasic text-white bg-gray-200">
            {!isVideoActive ? (
              <NoRemoteVideoView
                remoteAttendeeId={remoteAttendeeId}
                hasRemoteParticipantLeft={hasRemoteParticipantLeft}
                attendeeName={attendeeName}
                participantType="provider"
                isSpeaking={isSpeaking}
              />
            ) : (
              <span className="text-2xl">Waiting for the client to join...</span>
            )}
          </div>
        }
      />
      <div className='flex items-center justify-center w-full h-16 border-t-2 bg-gray-500 dark:bg-gray-800'>
        <BackgroundBlurProvider className='w-screen'>
          <ControlBar layout="undocked-horizontal" showLabels className='flex flex-col justify-between items-center w-full'>
            <div className="relative flex items-center">
              <AudioInputControl className='md:text-4xl' />
              <button
                type="button"
                aria-label="Audio Info"
                className=" focus:outline-none md:block hidden"
                onMouseEnter={handleInfoEnter}
                onMouseLeave={handleInfoLeave}
              >
                <InfoIcon />
              </button>
              {showInfo && (
                <div
                  ref={infoRef}
                  className="absolute left-1/2 bottom-full z-50  mt-2 w-64 -translate-x-1/2 rounded-md bg-white p-4 text-sm text-gray-800 shadow-lg border border-blue-200"
                  onMouseEnter={handleInfoEnter}
                  onMouseLeave={handleInfoLeave}
                >
                  {/* <div className="font-semibold mb-1 text-blue-700">Audio/Mic Help</div> */}
                  <div className='text-xs'>Having mic or audio issues, kindly update your device audio drivers or reach out to <a href="mailto:support@templehs.com" className="text-blue-600 underline">support@templehs.com</a></div>
                </div>
              )}
            </div>
            <VideoInputBackgroundBlurControl className='md:text-4xl text-black flex flex-col' />
            <ControlBarButton {...hangUpButtonProps} className='dark:text-white text-black text-pretty' />
            <ControlBarButton {...recordCallUpButtonProps} className='dark:text-white text-black items-center text-[11px] flex' />
            {isRecording ? <span className='text-green-500 text-[12px] font-gentiumBasic'>{callduration}</span> : null}
          </ControlBar>
        </BackgroundBlurProvider>
      </div>
    </div>
  );
}
